// src/components/facilityTransfer/FacilityTransfersPanel.jsx
//
// Manager board: each facility's latest handover to its real owner, and how
// far the invited owner got - Keycloak account created, e-mail verified,
// name filled in, invitation accepted (backend utils/facilityTransfer.ts
// transfersOverview). Admins only. "Resend" e-mails a new link (valid 14
// days again, the old one stops working) for a pending or expired invitation.
import React, { useCallback, useEffect, useState } from 'react';
import { Paper, Typography, Chip, Stack, Button, Alert, Box } from '@mui/material';
import { transfersOverview, resendTransfer, transferErrorText } from '../../utils/facilityTransferApi';

const date = (d) => new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
const STATUS = {
  PENDING: { label: 'Invitation sent', color: 'warning' },
  ACCEPTED: { label: 'Accepted', color: 'success' },
  EXPIRED: { label: 'Expired', color: 'default' },
  CANCELLED: { label: 'Cancelled', color: 'default' },
};

/** ✓ / ✗ step chip. */
const Step = ({ ok, label, title }) => (
  <Chip size="small" variant={ok ? 'filled' : 'outlined'} color={ok ? 'success' : 'default'}
    label={`${ok ? '✓' : '✗'} ${label}`} title={title} />
);

export default function FacilityTransfersPanel({ getToken }) {
  const [rows, setRows] = useState(null);
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(() => {
    setError('');
    transfersOverview(getToken()).then((r) => setRows(r.transfers)).catch(() => setError('Could not load the transfers.'));
  }, [getToken]);
  useEffect(() => { load(); }, [load]);

  const resend = async (t) => {
    if (!window.confirm(`Send a new transfer e-mail to ${t.toEmail}? The previous link will stop working.`)) return;
    setBusyId(t.id); setError(''); setInfo('');
    try {
      const r = await resendTransfer(t.id, getToken());
      setInfo(r.emailSent === false
        ? `New link created for ${t.toEmail}, but the e-mail could not be sent (mail server).`
        : `Transfer e-mail sent again to ${t.toEmail}.`);
      load();
    } catch (e) { setError(transferErrorText(e)); } finally { setBusyId(null); }
  };

  return (
    <Paper elevation={3} sx={{ p: 3, mb: 4 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
        <Typography variant="h6">Facility transfers</Typography>
        <Button size="small" onClick={load}>Refresh</Button>
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Facilities handed over by an ambassador: where the invited owner stands (Keycloak account, verified e-mail,
        name filled in, invitation accepted). Latest invitation per facility.
      </Typography>
      {error && <Alert severity="warning" sx={{ mb: 1 }} onClose={() => setError('')}>{error}</Alert>}
      {info && <Alert severity="success" sx={{ mb: 1 }} onClose={() => setInfo('')}>{info}</Alert>}
      {rows && rows.length === 0 && <Typography color="text.secondary">No transfer yet.</Typography>}
      {rows && rows.length > 0 && (
        <Box sx={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#f5f5f5' }}>
                {['Facility', 'Invited owner', 'Invitation', 'Account'].map((h) => (
                  <th key={h} style={{ padding: 8, textAlign: 'left' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((t) => {
                const st = STATUS[t.status] ?? STATUS.CANCELLED;
                const a = t.account;
                return (
                  <tr key={t.id} style={{ borderTop: '1px solid #e0e0e0' }}>
                    <td style={{ padding: 8 }}>
                      #{t.facilityId} {t.facilityName}
                      {!t.approved && <Chip size="small" label="not approved" sx={{ ml: 1 }} />}
                    </td>
                    <td style={{ padding: 8 }}>
                      {t.toEmail}
                      <Typography variant="caption" display="block" color="text.secondary">
                        current owner: {t.currentOwner}
                      </Typography>
                    </td>
                    <td style={{ padding: 8 }}>
                      <Chip size="small" color={st.color} label={st.label} />
                      <Typography variant="caption" display="block" color="text.secondary">
                        {t.status === 'ACCEPTED' ? `on ${date(t.acceptedAt)}` : `sent ${date(t.createdAt)}`}
                        {t.status === 'PENDING' && `, expires ${date(t.expiresAt)}`}
                      </Typography>
                      {(t.status === 'PENDING' || t.status === 'EXPIRED') && (
                        <Button size="small" sx={{ mt: 0.5, px: 0, minWidth: 0 }} disabled={busyId === t.id} onClick={() => resend(t)}>
                          {busyId === t.id ? 'Sending…' : 'Resend e-mail'}
                        </Button>
                      )}
                    </td>
                    <td style={{ padding: 8 }}>
                      {!t.accountChecked ? (
                        <Typography variant="body2" color="text.secondary">Unknown (Keycloak not reachable)</Typography>
                      ) : (
                        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                          <Step ok={!!a} label={a?.createdAt ? `Account ${date(a.createdAt)}` : 'Account'}
                            title={a ? 'Keycloak account created' : 'No Keycloak account with this e-mail yet'} />
                          <Step ok={!!a?.emailVerified} label="E-mail verified" />
                          <Step ok={!!a?.profileComplete}
                            label={a?.profileComplete ? `${a.firstName} ${a.lastName}` : 'Name'}
                            title="First and last name filled in" />
                        </Stack>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </Box>
      )}
    </Paper>
  );
}
